import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n/I18nContext";
import { LanguageSwitcher } from "../components/nivara/LanguageSwitcher";
import { toast } from "sonner";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/a8eea4b1-677b-4892-819c-00b0a2a4bc7e/images/119d03132e95e62a31040fc0dac96707989a4cabfffe8df9205b7dac9b7540b9.png";

export default function LoginPage() {
  const { login } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(identifier.trim().toLowerCase(), password);
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    toast.success(t("auth.login.welcome_back"));
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0F4C3A] text-white relative">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Nivara" className="h-10 w-10" />
            <div>
              <div className="font-heading text-xl font-semibold">NIVARA</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mt-1">{t("brand.foundation")}</div>
            </div>
          </Link>
          <LanguageSwitcher variant="dark" />
        </div>
        <div>
          <h1 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
            {t("auth.login.poetry_1")} <br /><span className="italic text-[#D4AF37]">{t("auth.login.poetry_2")}</span>
          </h1>
          <p className="mt-6 text-[#CBE8DA] text-lg max-w-md">
            {t("auth.login.poetry_text")}
          </p>
        </div>
        <div className="text-xs tracking-[0.25em] uppercase text-[#94A3B8]">
          {t("brand.locations")}
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-12 relative">
        <div className="absolute top-5 right-5 lg:hidden">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-10">
            <img src={LOGO_URL} alt="Nivara" className="h-9 w-9" />
            <div className="font-heading text-lg font-semibold text-[#0F4C3A]">NIVARA</div>
          </Link>
          <h2 className="font-heading text-3xl font-medium text-[#0F4C3A]">{t("auth.login.title")}</h2>
          <p className="mt-2 text-[#475569]">{t("auth.login.subtitle")}</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div>
              <label className="nv-label block mb-2">{t("auth.login.identifier")}</label>
              <input
                type="text"
                required
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                data-testid="login-identifier-input"
                className="w-full px-4 py-3 border border-[#E2E8E4] rounded-md focus:outline-none focus:border-[#0F4C3A] transition font-body"
                placeholder={t("auth.login.identifier_placeholder")}
              />
            </div>
            <div>
              <label className="nv-label block mb-2">{t("auth.login.password")}</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full px-4 py-3 border border-[#E2E8E4] rounded-md focus:outline-none focus:border-[#0F4C3A] transition font-body"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full nv-btn-primary py-3.5 rounded-md font-medium disabled:opacity-50"
            >
              {loading ? t("auth.login.submitting") : t("auth.login.submit")}
            </button>
          </form>

          <div className="mt-8 text-sm text-[#475569] text-center">
            {t("auth.login.no_account_text")}{" "}
            <Link to="/#interest" className="text-[#0F4C3A] font-medium underline underline-offset-4 hover:text-[#D4AF37]" data-testid="login-interest-link">
              {t("auth.login.interest_link")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
